import React from "react";
import { createBlankExpenseItem, ExpenseItem } from "../../shared/expenseItems";
import OcrReviewFlag from "./OcrReviewFlag";
import { receiptFieldClass } from "./receiptReviewTypes";

interface ReceiptLineItemsProps {
  currency: string;
  items: ExpenseItem[];
  onChange: (items: ExpenseItem[]) => void;
}

export default function ReceiptLineItems({ currency, items, onChange }: ReceiptLineItemsProps) {
  const updateItem = (index: number, name: keyof ExpenseItem, value: string) => {
    onChange(
      items.map((item, itemIndex) => (itemIndex === index ? { ...item, [name]: value } : item))
    );
  };

  return (
    <div className="mt-7">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold">Line items</h4>
        <button
          type="button"
          onClick={() => onChange([createBlankExpenseItem(currency), ...items])}
          className="text-sm font-medium text-primary-600"
        >
          + Add item
        </button>
      </div>
      <div className="mt-3 space-y-3">
        {items.length === 0 && (
          <p className="text-sm text-gray-500">
            No line items detected. You can add them manually.
          </p>
        )}
        {items.map((item, index) => (
          <div
            key={item.client_id || item.id || index}
            className="grid grid-cols-12 items-end gap-2 rounded-md border p-3"
          >
            <label className="col-span-5 text-xs font-medium">
              Description
              <OcrReviewFlag confidence={item.metadata?.confidence} />
              <input
                required
                className={receiptFieldClass}
                value={item.name}
                onChange={(event) => updateItem(index, "name", event.target.value)}
              />
            </label>
            <label className="col-span-2 text-xs font-medium">
              Quantity
              <input
                type="number"
                min="0.001"
                step="0.001"
                className={receiptFieldClass}
                value={item.quantity}
                onChange={(event) => updateItem(index, "quantity", event.target.value)}
              />
            </label>
            <label className="col-span-2 text-xs font-medium">
              Unit price
              <input
                type="number"
                min="0"
                step="0.01"
                className={receiptFieldClass}
                value={item.unit_price ?? ""}
                onChange={(event) => updateItem(index, "unit_price", event.target.value)}
              />
            </label>
            <label className="col-span-2 text-xs font-medium">
              Total
              <input
                type="number"
                min="0"
                step="0.01"
                className={receiptFieldClass}
                value={item.total_price ?? ""}
                onChange={(event) => updateItem(index, "total_price", event.target.value)}
              />
            </label>
            <button
              type="button"
              aria-label={`Remove ${item.name || "item"}`}
              onClick={() => onChange(items.filter((_, itemIndex) => itemIndex !== index))}
              className="col-span-1 pb-2 text-red-600"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
